import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useContext, useState } from "react";

import { Skeleton, Tooltip } from "@mui/material";
import BrandAuditIndexBreakups from "../../../Component/BrandIndexHistory/BrandAuditIndexBreakups";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import Modal from "../../../Component/Modal/Modal";
import WeightedDonutChart from "../../../Component/WeightedDonutChart/WeightedDonutChart";
import { DiscoveryContext } from "../discovery-context";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import MUIIconButton from "./../../../Component/UI/MUIIconButton/MUIIconButton";
import ProductVariant from "./../ProductVariant";
import classes from "./CompetitorItem.module.css";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import {
  calcBrandIndexScore,
  getBrandPillarsAvg,
  getProductDimensionsAvg,
  getPurchaseCriteriaAvg,
  RBI_COLORS,
  RBI_WEIGHTS,
} from "../../../utils/brandIndexCalc";

function extractDomain(input) {
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) {
    return input;
  }
  try {
    if (!/^https?:\/\//i.test(input)) {
      input = "http://" + input;
    }
    const urlObject = new URL(input);
    let hostname = urlObject.hostname;
    const parts = hostname.split(".");

    if (parts.length > 2) {
      hostname = parts.slice(-2).join(".");
    }

    return hostname;
  } catch (error) {
    console.error("Invalid input:", error.message);
    return null;
  }
}

export default function CompetitorItem({
  competitor,
  brandIndex,
  brand,
  llm,
  category,
  market,
  persona,
  ctx,
}) {
  const [showBrandIndexBreakups, setShowBrandIndexBreakups] = useState(false);

  const _defaultCtx = useContext(DiscoveryContext);
  const discoveryCtx = ctx || _defaultCtx;

  const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";
  let animation = discoveryCtx?.state?.showAnimation ?? false;
  const selectedInput = {
        llm,
        market:"General Market",
        category,
        brand: discoveryCtx.entity,
        product: null,
        persona,
        geography: market == "General Market" ? "General Location":market
  }
  const ctxCompetitor = discoveryCtx.competitors[category]
    .filter((comp) => comp.name != brand)
    .find((comp) => comp.name === competitor);

  let domain = "";
  if (ctxCompetitor && ctxCompetitor?.domain) {
    domain = extractDomain(ctxCompetitor?.domain);
  }

  const variants = ctxCompetitor?.variants
    ? ctxCompetitor.variants.filter((variant) => variant.active)
    : [];

  function LoadingSkeletion() {
    return (
      <>
        <Skeleton
          variant="rectangular"
          style={{
            zIndex: 1,
            flexGrow: 1,
            height: 220,
            flexBasis: "calc(50% - 12.5px)",
            backgroundColor: "rgb(0 0 0 / 11%)",
          }}
        />
      </>
    );
  }

  let cardContent = "";
  let loading = true;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus;

  if (competitor && !hasError && brandIndex?.total) {
    cardContent = (
      <>
        {!competitor && (
          <div className={classes.add_container}>
            <MUIIconButton size="large">
              <AddCircleOutlineOutlinedIcon
                style={{ fontSize: 40 }}
                fontSize="large"
              />
            </MUIIconButton>
          </div>
        )}
        {brandIndex?.extras && (
          <MUIIconButton
            data-html2canvas-ignore
            onClick={() => setShowBrandIndexBreakups(true)}
            className={classes.settings}
          >
            <InfoOutlinedIcon />
          </MUIIconButton>
        )}
        {/* <Tooltip title={brandIndex.name} placement="top" componentsProps={{ tooltip: { style: { display: 'inline-flex' } } }}> */}
        <div className={classes.header}>
          <Tooltip title={brandIndex.name} placement="top">
          {/* <img
            data-html2canvas-ignore
            className={classes.logo}
            src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
          /> */}
                <BrandAvatar
                      domainName={domain}
                      brandName={competitor}
              />
           </Tooltip>
            <Tooltip title={brandIndex.name} placement="top">
          <div className={classes.title_container}>   
            <span className={classes.title}>{competitor}</span>
            {variants.length > 0 && (
              <ProductVariant variants={variants} editable={false}>
                <span className={classes.variant_text}>
                  {variants.length} variants
                </span>
              </ProductVariant>
            )}
          </div>
           </Tooltip>
        </div>
        {/* </Tooltip> */}
        <div className={classes.content}>
          {(() => {
            const isProduct = discoveryCtx.type === "product";
            const coreAttributesData = discoveryCtx.core_attributes?.[llm]?.[category]?.[market]?.[persona];
            const categoryDimensionsData = discoveryCtx.category_dimensions?.[llm]?.[category]?.[market]?.[persona];
            const taxonomiesData = discoveryCtx.taxonomies?.[llm]?.[category]?.[market]?.[persona];
            const brandPillarsAvg = isProduct
              ? getProductDimensionsAvg(competitor, taxonomiesData)
              : getBrandPillarsAvg(competitor, coreAttributesData);
            const purchaseCriteriaAvg = getPurchaseCriteriaAvg(competitor, categoryDimensionsData);
            const { total: rbiTotal, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions } =
              calcBrandIndexScore({ rank: brandIndex?.rank, favorability: brandIndex?.favorability, brandPillarsAvg, purchaseCriteriaAvg });
            const SCORE_CARDS = [
              { key: "visibility",       label: ENTITY_LABELS.rank,                                  weight: RBI_WEIGHTS.visibility,       raw: brandIndex?.rank,        max: 50 },
              { key: "sentiment",        label: "Sentiment",                                         weight: RBI_WEIGHTS.sentiment,        raw: brandIndex?.favorability, max: 50 },
              { key: "brandPillars",     label: isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar,  weight: RBI_WEIGHTS.brandPillars,     raw: brandPillarsAvg,          max: 10 },
              { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,                      weight: RBI_WEIGHTS.purchaseCriteria, raw: purchaseCriteriaAvg,      max: 10 },
            ];
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "0 4px" }}>
                <div style={{ flex: "0 0 58%", minWidth: 0 }}>
                  <WeightedDonutChart
                    total={rbiTotal}
                    visNorm={visNorm}
                    sentNorm={sentNorm}
                    pillarsNorm={pillarsNorm}
                    purchaseNorm={purchaseNorm}
                    size="medium"
                    showLegend={false}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  {SCORE_CARDS.map((c) => {
                    const color = RBI_COLORS[c.key];
                    const weightPct = Math.round(c.weight * 100);
                    const rawDisplay = c.raw != null ? (c.max === 10 ? Number(c.raw).toFixed(1) : c.raw) : "—";
                    return (
                      <div key={c.key} style={{ background: "#fff", borderRadius: 8, padding: "8px", border: `1.5px solid ${color}33`, display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color, flexShrink: 0, display: "inline-block" }} />
                          <Tooltip title={c.label} placement="top" enterDelay={400}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#2d3748", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                          </Tooltip>
                          <span style={{ fontSize: 10, color: "#8a94a6", flexShrink: 0 }}>{weightPct}%</span>
                        </div>
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <span style={{ fontSize: 18, fontWeight: 700, color }}>{rawDisplay}</span>
                          <span style={{ fontSize: 10, color: "#8a94a6" }}>/{c.max}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </>
    );
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }


  if (competitor && hasError) {
    cardContent = (
      <ErrorView
        title="Error Fetching Competitor"
        message="Request Failed"
      />
    );
  }


  let compeititorCard = "";
  if (cardContent) {
    compeititorCard = (
      <MUICard
        variant="elevated"
        elevation={0}
        borderRadius={3}
        className={classes.card}
        width={"90%"}
      >
        {cardContent}
      </MUICard>
    );
  }
  return (
    <div
      className={classes.container}
      style={{
        backgroundColor: compeititorCard ? "#eff4f8" : "inherit",
      }}
    >
      <Modal
        width={1200}
        open={showBrandIndexBreakups}
        handleClose={() => setShowBrandIndexBreakups(false)}
      >
        <BrandAuditIndexBreakups
          data={brandIndex?.extras}
          isOpen={showBrandIndexBreakups}
          type={discoveryCtx.type}
          llm={llm}
          category={category}
          market={market}
          persona={persona}
          selectedInput={selectedInput}
          brandProduct={ctxCompetitor?.name}
          compIndexScoreMetaData={brandIndex ? [brandIndex] : []}
          ctx={ctx ? discoveryCtx : null}
        />
      </Modal>
      {compeititorCard}
      {!brandIndex?.total && loading && !hasError && <LoadingSkeletion />}
    </div>
  );
}
